import { useEffect, useState, useCallback, useRef } from "react";
import { api, API, STATUS_META, timeAgo } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { SettingsDialog } from "@/components/SettingsDialog";
import { TestConsole } from "@/components/TestConsole";
import { FlaskConical, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Flame, Settings, Search, Inbox, FileText, Download, Bell, ShieldAlert,
  Check, X, Send, User, BarChart3, RefreshCw, Clock, Mic,
} from "lucide-react";
import { toast } from "sonner";

const AVATARS = [
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBkaXZlcnNlJTIwaGVhZHNob3QlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODc2ODg3MDl8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBkaXZlcnNlJTIwaGVhZHNob3QlMjBwb3J0cmFpdHxlbnwwfHx8fDE3ODc2ODg3MDl8MA&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/33646670/pexels-photo-33646670.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.pexels.com/photos/29852895/pexels-photo-29852895.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];
const avatarFor = (id) => AVATARS[(id?.charCodeAt(0) || 0) % AVATARS.length];

const FILTERS = ["all", "new", "awaiting_resume", "awaiting_voice", "complete", "review", "interview", "rejected"];
const CHANNEL_LABEL = { chat: "Web chat", form: "Application form", messenger: "Messenger" };

export default function AdminDashboard() {
  const [candidates, setCandidates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [recruiterMsg, setRecruiterMsg] = useState("");
  const [notes, setNotes] = useState("");
  const transcriptRef = useRef(null);
  const loadedNotesId = useRef(null);

  const loadCandidates = useCallback(async () => {
    const r = await api.get("/candidates", { params: { status: filter, search } });
    setCandidates(r.data);
    if (!selectedId && r.data.length) setSelectedId(r.data[0].id);
  }, [filter, search, selectedId]);

  const loadAnalytics = useCallback(async () => {
    const r = await api.get("/analytics");
    setAnalytics(r.data);
  }, []);

  const loadNotifications = useCallback(async () => {
    const r = await api.get("/notifications");
    setNotifications(r.data);
  }, []);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    const r = await api.get(`/candidate/${id}`);
    setDetail(r.data);
    if (loadedNotesId.current !== id) {
      setNotes(r.data.candidate.notes || "");
      loadedNotesId.current = id;
    }
  }, []);

  useEffect(() => { loadCandidates(); loadAnalytics(); loadNotifications(); }, [filter, search]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  // light polling for live inbox
  useEffect(() => {
    const t = setInterval(() => { loadCandidates(); loadAnalytics(); loadNotifications(); if (selectedId) loadDetail(selectedId); }, 8000);
    return () => clearInterval(t);
  }, [filter, search, selectedId]);

  useEffect(() => {
    setTimeout(() => transcriptRef.current?.scrollTo({ top: transcriptRef.current.scrollHeight }), 100);
  }, [detail?.messages?.length]);

  const c = detail?.candidate;

  const patch = async (data) => {
    const r = await api.patch(`/candidate/${c.id}`, data);
    setDetail((p) => ({ ...p, candidate: r.data }));
    loadCandidates(); loadAnalytics();
    return r.data;
  };

  const toggleTakeover = async () => {
    await patch({ human_takeover: !c.human_takeover });
    await loadDetail(c.id);
    toast.success(!c.human_takeover ? "Human takeover engaged" : "Bot resumed");
  };

  const setDecision = async (decision) => {
    await patch({ decision, status: decision });
    toast.success(decision === "interview" ? "Marked for interview" : "Candidate rejected");
  };

  const setStatus = async (status) => { await patch({ status }); toast.success("Status updated"); };

  const saveNotes = async () => { await patch({ notes }); toast.success("Notes saved"); };

  const unreadCount = notifications.filter((n) => !n.read).length;
  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    loadNotifications();
  };
  const openNotification = (n) => { setSelectedId(n.candidate_id); markAllRead(); setNotifOpen(false); };

  const sendRecruiter = async () => {
    if (!recruiterMsg.trim()) return;
    await api.post(`/candidate/${c.id}/recruiter-message`, { content: recruiterMsg });
    setRecruiterMsg("");
    await loadDetail(c.id);
  };

  const sendReminder = async () => {
    await api.post(`/candidate/${c.id}/reminder`);
    await loadDetail(c.id);
    toast.success("Reminder sent");
  };

  const sendScript = async () => {
    try {
      const r = await api.post(`/candidate/${c.id}/send-script`);
      await loadDetail(c.id);
      toast.success(r.data.email_sent ? "Call script emailed & added to chat" : "Script added to chat (email skipped)");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send script");
    }
  };

  const resetConversation = async () => {
    if (!window.confirm("Reset this candidate's conversation? This clears their messages and status and re-sends the greeting.")) return;
    try {
      await api.post(`/candidate/${c.id}/reset`);
      await loadDetail(c.id);
      loadCandidates();
      toast.success("Conversation reset");
    } catch {
      toast.error("Could not reset");
    }
  };

  const download = (asset, label) => {
    if (!asset) return;
    window.open(`${API}/files/${asset.storage_path}`, "_blank");
    toast.success(`Opening ${label}`);
  };

  return (
    <div className="h-screen w-full flex bg-white overflow-hidden text-zinc-900">
      {/* Left rail */}
      <aside className="w-16 border-r border-zinc-200 flex flex-col items-center py-5 gap-6 bg-[#FAFAFA]">
        <div className="w-10 h-10 rounded-xl bg-[#E03C31] flex items-center justify-center" title="Ignite CX">
          <Flame className="w-6 h-6 text-white" strokeWidth={1.5} />
        </div>
        <nav className="flex flex-col gap-3 mt-2">
          <button className="w-10 h-10 rounded-lg bg-white border border-zinc-200 flex items-center justify-center text-[#E03C31]" title="Inbox" data-testid="nav-inbox">
            <Inbox className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button onClick={() => setSettingsOpen(true)} className="w-10 h-10 rounded-lg hover:bg-white border border-transparent hover:border-zinc-200 flex items-center justify-center text-zinc-500 transition-colors" title="Train AI" data-testid="nav-settings">
            <Settings className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button onClick={() => setTestOpen(true)} className="w-10 h-10 rounded-lg hover:bg-white border border-transparent hover:border-zinc-200 flex items-center justify-center text-zinc-500 transition-colors" title="Test Console" data-testid="nav-test-console">
            <FlaskConical className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </nav>
      </aside>

      {/* Inbox list */}
      <div className="w-80 border-r border-zinc-200 flex flex-col">
        <div className="px-4 pt-5 pb-3 border-b border-zinc-100">
          <div className="flex items-center justify-between">
            <h1 className="font-display text-xl font-semibold tracking-tight">Candidates</h1>
            <div className="flex items-center gap-3">
              <Popover open={notifOpen} onOpenChange={(o) => { setNotifOpen(o); if (o && unreadCount) markAllRead(); }}>
                <PopoverTrigger asChild>
                  <button className="relative w-8 h-8 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-500 transition-colors" data-testid="notifications-bell" title="Notifications">
                    <Bell className="w-4.5 h-4.5" strokeWidth={1.5} />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#E03C31] text-white text-[10px] font-semibold flex items-center justify-center" data-testid="notifications-count">{unreadCount}</span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
                  <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
                    <span className="font-display font-semibold text-sm">Notifications</span>
                    <span className="text-xs text-zinc-400">{notifications.length}</span>
                  </div>
                  <div className="max-h-80 overflow-y-auto thin-scroll">
                    {notifications.length === 0 && <div className="p-6 text-center text-sm text-zinc-400">No notifications yet.</div>}
                    {notifications.map((n) => (
                      <button key={n.id} onClick={() => openNotification(n)} data-testid={`notification-${n.id}`} className={`w-full text-left px-4 py-3 border-b border-zinc-50 hover:bg-zinc-50 transition-colors flex gap-3 ${!n.read ? "bg-red-50/40" : ""}`}>
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${n.type === "voice" ? "bg-purple-50 text-purple-600" : n.type === "complete" ? "bg-emerald-50 text-emerald-600" : n.type === "reminder" ? "bg-amber-50 text-amber-600" : "bg-blue-50 text-blue-600"}`}>
                          {n.type === "voice" ? <Mic className="w-4 h-4" strokeWidth={1.5} /> : n.type === "complete" ? <Check className="w-4 h-4" strokeWidth={1.5} /> : n.type === "reminder" ? <Bell className="w-4 h-4" strokeWidth={1.5} /> : <User className="w-4 h-4" strokeWidth={1.5} />}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm text-zinc-800 leading-snug">{n.message}</div>
                          <div className="text-[11px] text-zinc-400 mt-0.5">{timeAgo(n.created_at)}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <a href="/form" target="_blank" rel="noreferrer" className="text-xs text-[#E03C31] font-medium hover:underline" data-testid="open-apply-form">Form ↗</a>
              <a href="/candidate" target="_blank" rel="noreferrer" className="text-xs text-[#E03C31] font-medium hover:underline" data-testid="open-candidate-widget">Chat ↗</a>
            </div>
          </div>
          <div className="relative mt-3">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-zinc-400" />
            <Input data-testid="candidate-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name or email" className="pl-9 h-9 text-sm" />
          </div>
          <div className="flex gap-1.5 mt-3 overflow-x-auto thin-scroll pb-1">
            {FILTERS.map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap border transition-colors ${filter === f ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-500 border-zinc-200 hover:bg-zinc-50"}`}
              >
                {f === "all" ? "All" : (STATUS_META[f]?.label || f)}
              </button>
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto thin-scroll" data-testid="candidate-list">
          {candidates.length === 0 && (
            <div className="p-8 text-center text-sm text-zinc-400">No candidates yet.</div>
          )}
          {candidates.map((cand) => (
            <button
              key={cand.id}
              onClick={() => setSelectedId(cand.id)}
              data-testid={`candidate-item-${cand.id}`}
              className={`w-full text-left p-4 border-b border-zinc-100 hover:bg-zinc-50 transition-colors flex flex-col gap-2 ${selectedId === cand.id ? "bg-red-50/40 border-l-2 border-l-[#E03C31]" : ""}`}
            >
              <div className="flex items-center gap-3">
                <img src={avatarFor(cand.id)} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="font-medium text-sm truncate">{cand.name}</span>
                    {cand.flagged && <ShieldAlert className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                    {cand.human_takeover && <User className="w-3.5 h-3.5 text-[#E03C31] shrink-0" />}
                  </div>
                  <div className="text-xs text-zinc-400 truncate">{cand.email}</div>
                </div>
                <span className="text-[10px] text-zinc-400 shrink-0">{timeAgo(cand.last_message_at)}</span>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge status={cand.status} testid={`list-status-${cand.id}`} />
                <span className="text-[10px] text-zinc-400">{CHANNEL_LABEL[cand.channel] || "Web chat"}</span>
              </div>
            </button>
          ))}
        </div>
        {analytics && (
          <div className="border-t border-zinc-200 p-3 grid grid-cols-3 gap-2 bg-[#FAFAFA]" data-testid="analytics-bar">
            <Stat label="Total" value={analytics.total} icon={User} />
            <Stat label="Complete %" value={`${analytics.completion_rate}%`} icon={BarChart3} />
            <Stat label="Flagged" value={analytics.flagged} icon={ShieldAlert} />
          </div>
        )}
      </div>

      {/* Detail canvas */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {!c ? (
          <div className="flex-1 flex flex-col items-center justify-center text-zinc-300 gap-3">
            <Inbox className="w-12 h-12" strokeWidth={1} />
            <p className="text-sm">Select a candidate to review</p>
          </div>
        ) : (
          <>
            {c.human_takeover && <div className="h-1 bg-amber-400 w-full" />}
            {/* header */}
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center gap-4">
              <img src={avatarFor(c.id)} alt="" className="w-11 h-11 rounded-full object-cover" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-lg font-semibold truncate">{c.name}</h2>
                  <StatusBadge status={c.status} />
                  {c.flagged && <span className="text-[11px] text-amber-600 flex items-center gap-1"><ShieldAlert className="w-3.5 h-3.5" /> Flagged</span>}
                </div>
                <div className="text-xs text-zinc-400">{c.email} · Applied {timeAgo(c.created_at)}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-zinc-500">Human takeover</span>
                <Switch checked={!!c.human_takeover} onCheckedChange={toggleTakeover} data-testid="human-takeover-toggle" />
              </div>
            </div>

            <div className="flex-1 flex overflow-hidden">
              {/* transcript */}
              <div className="flex-1 flex flex-col border-r border-zinc-200 min-w-0">
                <div ref={transcriptRef} className="flex-1 overflow-y-auto thin-scroll p-5 space-y-3 bg-zinc-50/30" data-testid="transcript">
                  {detail.messages.map((m) => (
                    <TranscriptMsg key={m.id} m={m} />
                  ))}
                </div>
                <div className="p-3 border-t border-zinc-200">
                  {c.human_takeover ? (
                    <div className="flex gap-2">
                      <Input data-testid="recruiter-message-input" value={recruiterMsg} onChange={(e) => setRecruiterMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendRecruiter()} placeholder="Reply as recruiter..." className="flex-1" />
                      <Button onClick={sendRecruiter} className="bg-[#E03C31] hover:bg-[#C9362C]" data-testid="recruiter-send-btn"><Send className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-xs text-zinc-400 px-1">
                      <span className="flex items-center gap-1.5"><Flame className="w-3.5 h-3.5 text-[#E03C31]" /> Bot is handling this conversation</span>
                      <button onClick={sendReminder} className="flex items-center gap-1 text-[#E03C31] font-medium hover:underline" data-testid="send-reminder-btn"><Bell className="w-3.5 h-3.5" /> Send reminder</button>
                    </div>
                  )}
                </div>
              </div>

              {/* right info column */}
              <div className="w-80 overflow-y-auto thin-scroll p-5 space-y-6 shrink-0" data-testid="candidate-detail-panel">
                {/* assets */}
                <Section title="Submissions">
                  <AssetRow label="Resume" received={c.resume_received} asset={c.resume} onDownload={() => download(c.resume, "resume")} icon={FileText} testid="resume" />
                  <VoiceRow received={c.voice_received} asset={c.voice} testid="voice" />
                  <Button onClick={sendScript} variant="outline" size="sm" className="mt-1 w-full gap-2" data-testid="send-script-btn"><Send className="w-4 h-4" /> Email call script to candidate</Button>
                  <Button onClick={resetConversation} variant="ghost" size="sm" className="mt-1 w-full gap-2 text-zinc-500 hover:text-red-600" data-testid="reset-conversation-btn"><RotateCcw className="w-4 h-4" /> Reset conversation</Button>
                </Section>

                {/* decision */}
                <Section title="Decision">
                  <div className="grid grid-cols-2 gap-2">
                    <Button onClick={() => setDecision("interview")} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="decide-interview"><Check className="w-4 h-4" /> Interview</Button>
                    <Button onClick={() => setDecision("rejected")} variant="outline" className="border-zinc-200 text-zinc-700 gap-1.5 hover:bg-red-50 hover:text-red-600" data-testid="decide-reject"><X className="w-4 h-4" /> Reject</Button>
                  </div>
                  <div className="mt-3">
                    <label className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Set status</label>
                    <Select value={c.status} onValueChange={setStatus}>
                      <SelectTrigger className="mt-1.5 h-9" data-testid="status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FILTERS.filter((f) => f !== "all").map((s) => (
                          <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{STATUS_META[s]?.label || s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </Section>

                {/* notes */}
                <Section title="Recruiter Notes">
                  <Textarea data-testid="notes-textarea" value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="Add private notes..." className="text-sm" />
                  <Button onClick={saveNotes} variant="outline" size="sm" className="mt-2 w-full" data-testid="save-notes-btn">Save notes</Button>
                </Section>

                {/* audit */}
                <Section title="Activity Log">
                  <div className="space-y-2">
                    {detail.audit?.slice(0, 12).map((a) => (
                      <div key={a.id} className="flex items-start gap-2 text-xs" data-testid="audit-row">
                        <Clock className="w-3 h-3 text-zinc-300 mt-0.5 shrink-0" />
                        <div>
                          <span className="text-zinc-700 font-medium">{a.action.replace(/_/g, " ")}</span>
                          {a.detail && <span className="text-zinc-400"> · {a.detail}</span>}
                          <div className="text-[10px] text-zinc-300">{timeAgo(a.created_at)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Section>
              </div>
            </div>
          </>
        )}
      </main>

      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <TestConsole open={testOpen} onOpenChange={setTestOpen} />
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="flex flex-col items-center justify-center py-1.5 rounded-lg bg-white border border-zinc-100">
    <span className="font-display text-base font-semibold text-zinc-900">{value}</span>
    <span className="text-[10px] text-zinc-400 uppercase tracking-wide">{label}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div>
    <div className="text-xs font-semibold tracking-[0.05em] uppercase text-zinc-500 mb-2.5">{title}</div>
    {children}
  </div>
);

const TranscriptMsg = ({ m }) => {
  if (m.role === "system") {
    return <div className="flex justify-center"><span className="text-[11px] text-zinc-400 bg-white border border-zinc-100 px-3 py-1 rounded-full">{m.content}</span></div>;
  }
  const isCandidate = m.role === "candidate";
  return (
    <div className={`flex ${isCandidate ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[75%] px-3.5 py-2 text-sm whitespace-pre-wrap rounded-2xl ${
        isCandidate ? "bg-white border border-zinc-200 rounded-tl-sm" :
        m.role === "recruiter" ? "bg-amber-100 text-amber-900 border border-amber-200 rounded-tr-sm" :
        "bg-[#E03C31] text-white rounded-tr-sm"
      }`}>
        {m.role === "recruiter" && <div className="text-[10px] font-semibold uppercase opacity-70 mb-0.5">You</div>}
        {m.role === "bot" && <div className="text-[10px] font-semibold uppercase opacity-70 mb-0.5">Ignite AI</div>}
        {m.content}
      </div>
    </div>
  );
};

const AssetRow = ({ label, received, asset, onDownload, icon: Icon, testid }) => (
  <div className={`flex items-center gap-3 p-3 rounded-lg border mb-2 ${received ? "bg-white border-zinc-200" : "bg-zinc-50 border-dashed border-zinc-200"}`} data-testid={`asset-${testid}`}>
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${received ? "bg-emerald-50 text-emerald-600" : "bg-zinc-100 text-zinc-400"}`}>
      <Icon className="w-4 h-4" strokeWidth={1.5} />
    </div>
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium">{label}</div>
      <div className="text-xs text-zinc-400 truncate">{received ? asset?.original_filename : "Not received"}</div>
    </div>
    {received && <Button size="icon" variant="ghost" className="h-8 w-8 text-zinc-500" onClick={onDownload} data-testid={`download-${testid}`}><Download className="w-4 h-4" /></Button>}
  </div>
);

const VoiceRow = ({ received, asset, testid }) => (
  <div className={`p-3 rounded-lg border ${received ? "bg-white border-zinc-200" : "bg-zinc-50 border-dashed border-zinc-200"}`} data-testid={`asset-${testid}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${received ? "bg-emerald-50 text-emerald-600" : "bg-zinc-100 text-zinc-400"}`}>
        <FileText className="w-4 h-4" strokeWidth={1.5} />
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium">Voice Recording</div>
        <div className="text-xs text-zinc-400">{received ? "English audio submission" : "Not received"}</div>
      </div>
    </div>
    {received && asset && (
      <audio controls src={`${API}/files/${asset.storage_path}`} className="w-full h-9" data-testid="voice-player" />
    )}
  </div>
);
